import { ProviderRole } from '@bariendo/lib/constants';
import { getPrimaryProviderRole } from '@bariendo/lib/profile';
import { formatHumanName, resolveId } from '@medplum/core';
import { Practitioner, PractitionerRole, Reference, Resource } from '@medplum/fhirtypes';

/**
 * Who on our team holds which role — the lookup behind every teammate avatar.
 *
 * Two indexes because the timeline knows a teammate two ways: by Practitioner
 * reference (an Encounter participant, a Task owner) or by display name only
 * (an SMS thread's assignee names, the teammate who answered a call). Both
 * resolve to the same role, so both avatars wear the same ring.
 */
export interface ProviderDirectory {
  roleByPractitionerId: ReadonlyMap<string, ProviderRole>;
  roleByName: ReadonlyMap<string, ProviderRole>;
}

export const EMPTY_PROVIDER_DIRECTORY: ProviderDirectory = { roleByPractitionerId: new Map(), roleByName: new Map() };

/** Case- and whitespace-insensitive key for a display name. */
export function providerNameKey(name: string | undefined): string {
  return (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function buildProviderDirectory(
  practitionerRoles: PractitionerRole[],
  practitioners: Practitioner[]
): ProviderDirectory {
  const roleByPractitionerId = new Map<string, ProviderRole>();
  for (const practitionerRole of practitionerRoles) {
    const id = practitionerRole.practitioner ? resolveId(practitionerRole.practitioner) : undefined;
    const role = getPrimaryProviderRole(practitionerRole);
    // First PractitionerRole wins, matching the Task log's bucketing.
    if (id && role && !roleByPractitionerId.has(id)) {
      roleByPractitionerId.set(id, role);
    }
  }

  const roleByName = new Map<string, ProviderRole>();
  for (const practitioner of practitioners) {
    const role = practitioner.id ? roleByPractitionerId.get(practitioner.id) : undefined;
    const key = providerNameKey(formatHumanName(practitioner.name?.[0]));
    if (role && key && !roleByName.has(key)) {
      roleByName.set(key, role);
    }
  }

  return { roleByPractitionerId, roleByName };
}

export type ProviderSubject = Reference | Resource | string | undefined;

/**
 * The role for a teammate given however the caller knows them: a Reference,
 * a Practitioner resource, or a display name. A Reference that carries only a
 * `display` falls back to the name index.
 */
export function lookupProviderRole(directory: ProviderDirectory, subject: ProviderSubject): ProviderRole | undefined {
  if (!subject) {
    return undefined;
  }
  if (typeof subject === 'string') {
    return directory.roleByName.get(providerNameKey(subject));
  }
  if ('resourceType' in subject) {
    if (subject.resourceType !== 'Practitioner') {
      return undefined;
    }
    const byId = subject.id ? directory.roleByPractitionerId.get(subject.id) : undefined;
    return byId ?? directory.roleByName.get(providerNameKey(formatHumanName((subject as Practitioner).name?.[0])));
  }
  const id = subject.reference?.startsWith('Practitioner/') ? resolveId(subject) : undefined;
  const byId = id ? directory.roleByPractitionerId.get(id) : undefined;
  return byId ?? directory.roleByName.get(providerNameKey(subject.display));
}
